import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import type { EditorHost } from '@blocksuite/std';

import {
  addProperty,
  deleteColumn,
  updateCell,
} from '../../utils/block-utils.js';
import {
  dedupeRefs,
  RELATION_PROPERTY_TYPE,
  type RelationPropertyData,
  type RelationRawValue,
  type RelationRef,
} from './types.js';

const cellRefs = (
  db: DatabaseBlockModel,
  rowId: string,
  columnId: string
): RelationRawValue => {
  const raw = db.props.cells?.[rowId]?.[columnId]?.value;
  if (!Array.isArray(raw)) return [];
  return dedupeRefs(raw as RelationRawValue);
};

const writeRefs = (
  db: DatabaseBlockModel,
  rowId: string,
  columnId: string,
  next: RelationRawValue
) => {
  updateCell(db, rowId, { columnId, value: next });
};

const refKey = (ref: { docId: string; blockId: string }) =>
  `${ref.docId}::${ref.blockId}`;

const sameRefs = (a: RelationRawValue, b: RelationRawValue) => {
  if (a.length !== b.length) return false;
  const sa = new Set(a.map(refKey));
  for (const r of b) if (!sa.has(refKey(r))) return false;
  return true;
};

/**
 * Ensure that the target database has a column paired with the source column.
 * Returns the paired column id (existing or newly created).
 */
export const ensurePairColumn = (params: {
  sourceDocId: string;
  sourceBlockId: string;
  sourceColumnId: string;
  sourceColumnName: string;
  backRelationName?: string;
  targetDb: DatabaseBlockModel;
  existingPairedColumnId: string | null;
}): string | null => {
  const {
    sourceDocId,
    sourceBlockId,
    sourceColumnId,
    sourceColumnName,
    backRelationName,
    targetDb,
    existingPairedColumnId,
  } = params;

  if (existingPairedColumnId) {
    const existing = targetDb.props.columns.find(
      c => c.id === existingPairedColumnId
    );
    if (existing && existing.type === RELATION_PROPERTY_TYPE) {
      return existingPairedColumnId;
    }
  }

  const pairData: RelationPropertyData = {
    _v: 1,
    targetDocId: sourceDocId,
    targetBlockId: sourceBlockId,
    pairedColumnId: sourceColumnId,
  };
  const name =
    backRelationName && backRelationName.trim().length > 0
      ? backRelationName.trim()
      : `← ${sourceColumnName || 'Related'}`;
  const newColumnId = addProperty(targetDb, 'end', {
    type: RELATION_PROPERTY_TYPE,
    name,
    data: pairData as unknown as Record<string, unknown>,
  });
  return newColumnId;
};

/**
 * Remove the paired column on the target database (no-op if missing).
 */
export const removePairColumn = (
  targetDb: DatabaseBlockModel,
  pairedColumnId: string
) => {
  const existing = targetDb.props.columns.find(c => c.id === pairedColumnId);
  if (!existing) return;
  deleteColumn(targetDb, pairedColumnId);
};

/**
 * Sync back-relation cells on target side after a write to a source Relation cell.
 *
 * Diffs old vs new refs and updates target rows' paired cells. Idempotent: writes
 * only when the target value would actually change, preventing Yjs/signal loops.
 */
export const syncBackRelation = (params: {
  std: EditorHost['std'] | undefined;
  sourceDocId: string;
  sourceRowId: string;
  pairedColumnId: string;
  prevRefs: RelationRawValue;
  nextRefs: RelationRawValue;
}) => {
  const { std, sourceDocId, sourceRowId, pairedColumnId, prevRefs, nextRefs } =
    params;

  const prevKeys = new Set(prevRefs.map(refKey));
  const nextKeys = new Set(nextRefs.map(refKey));

  const added = nextRefs.filter(r => !prevKeys.has(refKey(r)));
  const removed = prevRefs.filter(r => !nextKeys.has(refKey(r)));

  const backRef: RelationRef = {
    _v: 1,
    docId: sourceDocId,
    blockId: sourceRowId,
  };

  // For each added ref, add backRef into target row's paired cell.
  for (const ref of added) {
    const doc = std?.workspace.getDoc(ref.docId);
    if (!doc || !doc.loaded) continue;
    const rowBlock = doc.getStore({ id: doc.id }).getBlock(ref.blockId)?.model;
    if (!rowBlock) continue;
    const parent = rowBlock.parent;
    if (!parent || parent.flavour !== 'affine:database') continue;
    const targetDb = parent as unknown as DatabaseBlockModel;

    const currentCellRefs = cellRefs(targetDb, ref.blockId, pairedColumnId);
    if (currentCellRefs.some(r => refKey(r) === refKey(backRef))) continue;
    const next = dedupeRefs([...currentCellRefs, backRef]);
    if (sameRefs(currentCellRefs, next)) continue;
    writeRefs(targetDb, ref.blockId, pairedColumnId, next);
  }

  for (const ref of removed) {
    const doc = std?.workspace.getDoc(ref.docId);
    if (!doc || !doc.loaded) continue;
    const rowBlock = doc.getStore({ id: doc.id }).getBlock(ref.blockId)?.model;
    if (!rowBlock) continue;
    const parent = rowBlock.parent;
    if (!parent || parent.flavour !== 'affine:database') continue;
    const targetDb = parent as unknown as DatabaseBlockModel;

    const currentCellRefs = cellRefs(targetDb, ref.blockId, pairedColumnId);
    const next = currentCellRefs.filter(r => refKey(r) !== refKey(backRef));
    if (sameRefs(currentCellRefs, next)) continue;
    writeRefs(targetDb, ref.blockId, pairedColumnId, next);
  }
};
